/**
 * `sku_tags.expires_at` (WEB-1564) is the last day on which a SKU's `preorder`
 * and `new-release` tags count. After it, the badge disappears here and the
 * shipping note disappears from the buyer emails — the backend applies the same
 * rule in `sku-shipping-note.util.ts`, so site and emails never disagree.
 *
 * Other tags (`tasting_ticket`, `hot_deal`, ...) are not governed by
 * `expires_at`. The expiry day itself still counts, in Munich time, and a date
 * that is not `YYYY-MM-DD` counts as expired.
 */
const EXPIRING_TAGS = ['preorder', 'new-release'];
const DAY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

const berlinDayFormatter = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

type SkuTagsJson = { tags?: unknown; expires_at?: unknown } | null | undefined;

const isExpired = (expiresAt: unknown, now: Date): boolean => {
  if (expiresAt === undefined || expiresAt === null || expiresAt === '') return false;
  if (typeof expiresAt !== 'string' || !DAY_PATTERN.test(expiresAt.trim())) return true;
  // Both sides are 'YYYY-MM-DD', so the string comparison is the date comparison.
  return berlinDayFormatter.format(now) > expiresAt.trim();
};

/** The SKU's tags with `expires_at` applied. Blank entries are dropped. */
export const getActiveSkuTags = (skuTags: SkuTagsJson, now: Date = new Date()): string[] => {
  if (!skuTags || !Array.isArray(skuTags.tags)) return [];
  const tags = skuTags.tags.filter((t): t is string => typeof t === 'string' && t.trim() !== '');
  return isExpired(skuTags.expires_at, now) ? tags.filter(t => !EXPIRING_TAGS.includes(t)) : tags;
};
